import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import api, { formatPrice, resolveImg, applyCouponLocal } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import CouponInput from "../components/CouponInput";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, coupon, refreshStock } = useCart();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
  });

  useEffect(() => {
    if (items.length === 0) navigate("/cart");
  }, [items, navigate]);

  useEffect(() => {
    refreshStock().then((notes) => notes.length && navigate("/cart"));
  }, [refreshStock, navigate]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, name: f.name || user.name, email: f.email || user.email }));
    }
  }, [user]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const { discount, shipping, total } = applyCouponLocal(subtotal, coupon);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout/create-session", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping: form,
        origin_url: window.location.origin,
        coupon_code: coupon?.code || null,
      });
      // Stash the order number for the success page
      sessionStorage.setItem("dc_last_order_number", data.order_number);
      window.location.href = data.url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.message) {
        toast.error(detail.message);
        await refreshStock();
        navigate("/cart");
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not start checkout");
      }
      setSubmitting(false);
    }
  };

  if (items.length === 0) return null;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-12 lg:py-20" data-testid="checkout-page">
      <p className="eyebrow mb-3">Checkout</p>
      <h1 className="font-serif text-5xl text-[var(--ink-forest)] tracking-tight mb-12">
        Almost there
      </h1>

      <form onSubmit={handleSubmit} className="grid lg:grid-cols-3 gap-10">
        {/* Form */}
        <div className="lg:col-span-2 space-y-8">
          <Section title="Contact">
            <Field label="Email" required>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="ck-input"
                data-testid="checkout-email"
              />
            </Field>
          </Section>

          <Section title="Shipping address">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name" required full>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="ck-input"
                  data-testid="checkout-name"
                />
              </Field>
              <Field label="Address line 1" required full>
                <input
                  type="text"
                  required
                  value={form.address_line1}
                  onChange={(e) => update("address_line1", e.target.value)}
                  className="ck-input"
                  data-testid="checkout-address1"
                />
              </Field>
              <Field label="Address line 2 (optional)" full>
                <input
                  type="text"
                  value={form.address_line2}
                  onChange={(e) => update("address_line2", e.target.value)}
                  className="ck-input"
                  data-testid="checkout-address2"
                />
              </Field>
              <Field label="City" required>
                <input
                  type="text"
                  required
                  value={form.city}
                  onChange={(e) => update("city", e.target.value)}
                  className="ck-input"
                  data-testid="checkout-city"
                />
              </Field>
              <Field label="State" required>
                <select
                  required
                  value={form.state}
                  onChange={(e) => update("state", e.target.value)}
                  className="ck-input"
                  data-testid="checkout-state"
                >
                  <option value="">Select…</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="ZIP code" required>
                <input
                  type="text"
                  required
                  value={form.zip}
                  onChange={(e) => update("zip", e.target.value)}
                  className="ck-input"
                  data-testid="checkout-zip"
                />
              </Field>
              <Field label="Phone (optional)">
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="ck-input"
                  data-testid="checkout-phone"
                />
              </Field>
            </div>
          </Section>
        </div>

        {/* Summary */}
        <div className="lg:sticky lg:top-28 self-start space-y-5">
          <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl p-6">
            <h3 className="font-serif text-xl text-[var(--ink-forest)] mb-4">Order summary</h3>
            <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
              {items.map((item) => (
                <div key={item.product_id} className="flex gap-3 text-sm">
                  <img src={resolveImg(item.image_url)} alt="" className="w-12 h-12 rounded-lg object-cover bg-[var(--muted)]" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[var(--ink-forest)] line-clamp-1">{item.name}</p>
                    <p className="text-xs text-[var(--muted-fg)]">Qty {item.quantity}</p>
                  </div>
                  <p className="text-[var(--ink-forest)] font-medium">{formatPrice(item.price * item.quantity)}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-[var(--border)] mt-5 pt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--muted-fg)]">Subtotal</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between" data-testid="checkout-discount-row">
                  <span className="text-[var(--muted-fg)]">Coupon ({coupon.code})</span>
                  <span className="text-[var(--terracotta)] font-semibold">−{formatPrice(discount)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-[var(--muted-fg)]">Shipping</span>
                <span className={shipping === 0 ? "text-[var(--terracotta)] font-semibold" : ""}>
                  {shipping === 0 ? "Free" : formatPrice(shipping)}
                </span>
              </div>
              <div className="flex justify-between font-bold text-base mt-2 pt-2 border-t border-[var(--border)]">
                <span>Total</span>
                <span data-testid="checkout-total">{formatPrice(total)}</span>
              </div>
            </div>

            <div className="mt-5">
              <CouponInput />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[var(--terracotta)] text-white rounded-full py-4 text-base font-semibold hover:bg-[var(--terracotta-deep)] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
            data-testid="pay-now-btn"
          >
            <Lock className="w-4 h-4" />
            {submitting ? "Redirecting…" : `Pay ${formatPrice(total)}`}
          </button>
          <p className="text-xs text-center text-[var(--muted-fg)]">
            Secured by Stripe. You&apos;ll be redirected to complete payment.
          </p>
        </div>
      </form>

      <style>{`
        .ck-input {
          width: 100%;
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 0.75rem 1rem;
          font-size: 0.9rem;
          color: var(--ink-forest);
          outline: none;
          transition: border-color 0.2s;
        }
        .ck-input:focus {
          border-color: var(--terracotta);
          box-shadow: 0 0 0 3px rgba(192, 90, 70, 0.12);
        }
      `}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-serif text-2xl text-[var(--ink-forest)] mb-4">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, children, required, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-xs font-medium text-[var(--ink-forest)] uppercase tracking-wide block mb-1.5">
        {label} {required && <span className="text-[var(--terracotta)]">*</span>}
      </span>
      {children}
    </label>
  );
}
